//#region src/index.mts
var Trie = class {
	constructor(parent) {
		this.parent = null;
		this.goto = /* @__PURE__ */ new Map();
		this.keywords = null;
		this.parent = parent ?? null;
	}
	can(s) {
		return this.goto.has(s);
	}
	go(s) {
		return this.goto.get(s);
	}
	define(s, next) {
		return this.goto.set(s, next);
	}
	undef(s) {
		this.goto.delete(s);
	}
	entries() {
		return this.goto.entries();
	}
	empty() {
		return this.keywords == null || this.keywords.size === 0;
	}
	contains(k) {
		return this.keywords != null && this.keywords.has(k);
	}
	add(k) {
		if (this.keywords == null) this.keywords = /* @__PURE__ */ new Set();
		this.keywords.add(k);
	}
	delete(k) {
		this.keywords?.delete(k);
		if (this.keywords?.size === 0) this.keywords = null;
	}
	values() {
		return this.keywords?.values() ?? [];
	}
	merge(t) {
		for (const keyword of t?.values() ?? []) this.add(keyword);
	}
	defunct() {
		return this.goto.size === 0 && this.empty();
	}
};
var AhoCorasick = class {
	constructor(keywords) {
		this.root = new Trie();
		this.failure_link = /* @__PURE__ */ new Map();
		for (const keyword of keywords) {
			let current = this.root;
			for (let i = 0; i < keyword.length; i++) {
				const ch = keyword[i];
				let next = current.go(ch) ?? new Trie(current);
				current.define(ch, next);
				current = next;
			}
			current.add(keyword);
		}
		{
			let top = 0;
			const queue = [];
			for (const [ch, next] of this.root.entries()) queue.push([next, ch]);
			while (top < queue.length) {
				const [current, ch] = queue[top++];
				const parent = current.parent;
				let failure = this.failure_link.get(parent) ?? null;
				while (failure != null && !failure.can(ch)) failure = this.failure_link.get(failure) ?? null;
				failure = failure?.go(ch) ?? this.root;
				this.failure_link.set(current, failure);
				current.merge(failure);
				for (const [ch, next] of current.entries()) queue.push([next, ch]);
			}
		}
	}
	hasKeywordInText(text) {
		let state = this.root;
		for (const ch of text) {
			if (!state.empty()) return true;
			while (!state.can(ch) && state !== this.root) state = this.failure_link.get(state);
			state = state.go(ch) ?? this.root;
		}
		return !state.empty();
	}
	matchInText(text) {
		const result = [];
		let state = this.root;
		for (let i = 0; i < text.length; i++) {
			for (const keyword of state.values()) {
				const begin = i - keyword.length;
				const end = i;
				result.push({
					begin,
					end,
					keyword
				});
			}
			const ch = text[i];
			while (!state.can(ch) && state !== this.root) state = this.failure_link.get(state);
			state = state.go(ch) ?? this.root;
		}
		for (const keyword of state.values()) {
			const begin = text.length - keyword.length;
			const end = text.length;
			result.push({
				begin,
				end,
				keyword
			});
		}
		return result;
	}
};
var DynamicAhoCorasick = class extends AhoCorasick {
	constructor(keywords) {
		super(keywords);
		this.invert_failure_link = /* @__PURE__ */ new Map();
		{
			let top = 0;
			const queue = [];
			for (const [_, next] of this.root.entries()) queue.push(next);
			while (top < queue.length) {
				const current = queue[top++];
				const failure = this.failure_link.get(current);
				if (failure == null) continue;
				if (!this.invert_failure_link.has(failure)) this.invert_failure_link.set(failure, /* @__PURE__ */ new Set());
				this.invert_failure_link.get(failure).add(current);
				for (const [_, next] of current.entries()) queue.push(next);
			}
		}
	}
	add(keyword) {
		let parent = this.root;
		for (let i = 0; i < keyword.length; i++) {
			const ch = keyword[i];
			const current = parent.go(ch) ?? new Trie(parent);
			parent.define(ch, current);
			if (i === keyword.length - 1) current.add(keyword);
			let failure = this.failure_link.get(parent) ?? null;
			while (failure != null && !failure.can(ch)) failure = this.failure_link.get(failure) ?? null;
			failure = failure?.go(ch) ?? this.root;
			this.failure_link.set(current, failure);
			if (!this.invert_failure_link.has(failure)) this.invert_failure_link.set(failure, /* @__PURE__ */ new Set());
			this.invert_failure_link.get(failure).add(current);
			current.merge(failure);
			if (!this.invert_failure_link.has(current)) this.invert_failure_link.set(current, /* @__PURE__ */ new Set());
			{
				let top = 0;
				const queue = [...this.invert_failure_link.get(parent) ?? []];
				while (top < queue.length) {
					const before = queue[top++];
					if (before.can(ch)) {
						const invert_failure = before.go(ch);
						this.failure_link.set(invert_failure, current);
						this.invert_failure_link.get(failure).delete(invert_failure);
						this.invert_failure_link.get(current).add(invert_failure);
						continue;
					}
					queue.push(...this.invert_failure_link.get(before) ?? []);
				}
			}
			parent = current;
		}
		{
			let top = 0;
			const queue = [parent];
			while (top < queue.length) {
				const current = queue[top++];
				for (const invert_failure of this.invert_failure_link.get(current)?.values() ?? []) {
					invert_failure.merge(current);
					queue.push(invert_failure);
				}
			}
		}
	}
	delete(keyword) {
		let target = this.root;
		for (let i = 0; i < keyword.length; i++) {
			const ch = keyword[i];
			if (!target.can(ch)) return;
			target = target.go(ch);
		}
		if (!target.contains(keyword)) return;
		target.delete(keyword);
		{
			let top = 0;
			const queue = [target];
			while (top < queue.length) {
				const current = queue[top++];
				for (const invert_failure of this.invert_failure_link.get(current)?.values() ?? []) {
					invert_failure.delete(keyword);
					queue.push(invert_failure);
				}
			}
		}
		for (let leaf = target, index = keyword.length - 1; leaf != null && leaf.defunct(); leaf = leaf.parent ?? null, index--) {
			leaf.parent?.undef(keyword[index]);
			const failure = this.failure_link.get(leaf);
			if (failure == null) break;
			this.invert_failure_link.get(failure).delete(leaf);
			for (const invert_failure of this.invert_failure_link.get(leaf) ?? []) {
				this.failure_link.set(invert_failure, failure);
				this.invert_failure_link.get(failure).add(invert_failure);
			}
			this.failure_link.delete(leaf);
			this.invert_failure_link.delete(leaf);
		}
	}
};
//#endregion
export { AhoCorasick, DynamicAhoCorasick };
